import {OwnerRegistrationData} from './../../../../models/registration-offer-data/owner-registration-data.model';
import {ConfigurationService} from './../../configuration/configuration.service';
import {InsurerRegistrationData} from './../../../../models/registration-offer-data/insurer-registration-data.model';
import {RegistrationService} from './../../registration.service';
import {CalculationService} from '../../calculation.service';
import {EventEmitter, Injectable} from '@angular/core';
import {InsurerDataSectionInterface} from '../../../api/sections-api/insurer-data-section.interface';
import {InsurerData} from '../../../../models/insurer-data.model';
import {CalculationStorageService} from '../../../../../shared/services/calculation-storage.service';
import {PeselService} from '../../../../../shared/services/pesel.service';
import {Dictionaries, UiConfig} from '../../../../configuration/ui-config';
import {InsuranceCompanyCode} from '../../../../../shared/models/insurance-company-code.enum';
import {ApplicationStateService} from '../../../../../shared/services/application-state.service';
import {
  AddressItemConfig,
  DictionaryMode,
  InsurerDataMode,
  InsurerDataModeConfig,
  RequiredStrategy
} from '../../../../../shared/sections/web/insurer-data-section/insurer-data-mode-config';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {AddressItem} from '../../../../models/address-base.model';
import {InsurerDataConsts} from '../../../../consts/insurer-data.consts';
import {FieldType} from '../../../../../shared/models/field-type';
import {RegistrationData} from '../../../../models/registration-data.model';
import {Dictionary} from '../../../../configuration/dictionary';
import {CoOwnerModel} from '../../../../models/owner-data/co-owner.model';
import {RegistrationStorageService} from '../../../../../shared/services/registration-storage.service';
import {CityItem, InsuranceCompanyApiService} from '../../../insurance-company-api.service';
import * as _ from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class InsurerDataFormStateService implements InsurerDataSectionInterface {

  updateDictionaryEvent: EventEmitter<UpdateDictionaryEvent> = new EventEmitter();
  streetRequiredStrategy: RequiredStrategy;
  private dictionaries: Dictionaries;
  private addressDataDictionaries: AddressDataDictionary[] = [];

  get insuranceCompanyCode(): InsuranceCompanyCode {
    return this.stateService.insuranceCompanyCode;
  }

  get sectionModeConfig(): InsurerDataModeConfig {
    return this.insuranceCompanyApiService.modeConfig;
  }

  get isDictionaryMode(): boolean {
    return this.sectionModeConfig.mode === InsurerDataMode.DICTIONARY;
  }

  get isStreetSelect(): boolean {
    return this.sectionModeConfig.mode === InsurerDataMode.DICTIONARY && this.sectionModeConfig.streetConfig.fieldType === FieldType.Select;
  }

  get isDictionaryModeDynamic(): boolean {
    return this.sectionModeConfig.dictionaryMode === DictionaryMode.DYNAMIC;
  }

  get separateInsurerAvailable(): boolean {
    return [InsuranceCompanyCode.TRASTI].includes(this.stateService.insuranceCompanyCode);
  }

  get separateInsurer(): boolean {
    return this.stateService.separateInsurer;
  }

  constructor(private calculation: CalculationService,
              private registration: RegistrationService,
              private configuration: ConfigurationService,
              private stateService: ApplicationStateService,
              private calculationStorage: CalculationStorageService,
              private registrationStorage: RegistrationStorageService,
              private insuranceCompanyApiService: InsuranceCompanyApiService) {
    this.dictionaries = this.configuration.uiConfig.dictionaries;
  }

  get countriesDict(): Dictionary {
    if (this.dictionaries) {
      return this.dictionaries[UiConfig.COUNTRIES_REGISTRATION];
    }
    return null;
  }

  get insurerData(): InsurerData {
    return this.calculation.insurerData;
  }

  get registrationData(): RegistrationData {
    return this.registration.registration;
  }

  get insurerRegistrationData(): InsurerRegistrationData {
    return this.registration.insurerData;
  }

  get ownerRegistrationData(): OwnerRegistrationData {
    return this.registration.ownerData;
  }

  get coownersRegistrationData(): any[] {
    return this.registration.ownerData.coOwners;
  }

  get coownersData(): CoOwnerModel[] {
    return this.calculation.ownerData.coOwners;
  }

  initCoOwners(): void {
    this.registration.initCoOwners(this.coownersData.length);
  }

  initContactDataFromCalculation(): void {
    this.registration.initContactDataFromCalculation();
  }

  changeNIP(NIP: string): void {
    this.insurerData!.nip = NIP;
    this.calculationStorage.updateCalculation();
  }

  // changeEmail(email: string): void {
  //   this.insurerData!.email = email;
  //   this.storage.updateCalculation();
  // }

  changePesel(pesel: string): void {
    this.insurerData!.pesel = pesel;
    let birthDateFromPesel = PeselService.getBirthdate(pesel);
    this.insurerData!.birthDate = birthDateFromPesel;
    this.calculationStorage.updateCalculation();
  }

  changeFirstName(firstName: string) {
    this.insurerData!.firstName = firstName;
    this.calculationStorage.updateCalculation();
  }

  changeLastName(lastName: string): void {
    this.insurerData!.lastName = lastName;
    this.calculationStorage.updateCalculation();
  }

  changePostalCode(postalCode: string): void {
    this.insurerData!.address.postalCode = postalCode;
    this.calculationStorage.updateCalculation();
  }

  changeCompanyName(companyName: string): void {
    this.insurerData!.companyName = companyName;
    this.calculationStorage.updateCalculation();
  }

  changePartyTypeCode(partyTypeCode: string): void {
    this.insurerData.partyTypeCode = partyTypeCode;
    // this.partyTypeCode = partyTypeCode;
    this.calculationStorage.updateCalculation();
  }

  // changeWork(work: string): void {
  //   this.insurerData!.work = work;
  //   this.storage.updateCalculation();
  // }

  changeBirthDate(birthDate: Date): void {
    this.insurerData!.birthDate = birthDate;
    this.calculationStorage.updateCalculation();
  }

  changeDrivingLicenseIssueYear(drivingLicenseIssueYear: number): void {
    this.insurerData!.drivingLicenseIssueYear = drivingLicenseIssueYear;
    this.calculationStorage.updateCalculation();
  }

  changeAdressRegistrationData(controlName: string, value: any, context: AddressDataContext): void {
    const { isCoOwner, coOwnerIndex } = context;
    const addressPropName = controlName.split('.')[1];
    if (!isCoOwner) {
      if (!this.stateService.separateInsurer) {
        this.insurerRegistrationData!.address[addressPropName] = value;
      }
      this.ownerRegistrationData!.address[addressPropName] = value;
    } else {
      this.coownersRegistrationData[coOwnerIndex]!.address[addressPropName] = value;
    }
    this.registrationStorage.updateRegistration();
    if (controlName === InsurerDataConsts.CITY) {
      if (this.sectionModeConfig.isCountyRequired) {
        const county = this.getDictionary(context).cities.initial.find(city => city.cityName === value).county;
        this.changeCounty(county, context);
      }
      if (this.isStreetSelect) {
        this.resetStreet(context);
        if (isNotNullOrUndefined(value)) {
          if (this.isDictionaryModeDynamic) {
            this.notifyAboutDictionaryUpdate(DictionaryType.CITIES, {...context, version: DictionaryVersion.INITIAL});
          }
          this.requestStreets({isCoOwner: isCoOwner, coOwnerIndex: coOwnerIndex, version: DictionaryVersion.INITIAL});
        }
      }
    } else if (controlName === InsurerDataConsts.STREET && this.isDictionaryModeDynamic) {
      if (isNotNullOrUndefined(value)) {
        this.notifyAboutDictionaryUpdate(DictionaryType.STREETS, { ...context, version: DictionaryVersion.INITIAL });
      }
    }
  }

  changeCounty(county: string, context: AddressDataContext): void {
    const { isCoOwner, coOwnerIndex } = context;
    if (!isCoOwner) {
      this.insurerRegistrationData!.address.county = county;
      this.ownerRegistrationData!.address.county = county;
    } else {
      this.coownersRegistrationData[coOwnerIndex]!.address.county = county;
    }
  }

  changeSeparateInsurer(separateInsurer: boolean): void {
    this.stateService.separateInsurer = separateInsurer;
    this.stateService.separateInsurer ? this.clearInsurerData() : this.setInsurerDataFromOwner();
  }

  init(): void {
    this.initRequiredStrategy();
  }

  initAddressDataDictionaries(): void {
    this.addressDataDictionaries = [];
    this.initAddressDataDictionary({ isCoOwner: false, version: DictionaryVersion.INITIAL });
    this.coownersData.forEach((coOwner, index) => this.initAddressDataDictionary({ isCoOwner: true, coOwnerIndex: index, version: DictionaryVersion.INITIAL }));
    if (this.canShowInitialDictionary(InsurerDataConsts.CITY)) {
      this.requestCitiesDictionaries();
    }
  }

  requestCities(context: AddressDataContext, cityName?: string): void {
    const { postalCode } = this.getCitiesRequestParams(context);
    this.insuranceCompanyApiService.findCities(postalCode, cityName).then(resp => {
      const items = resp.findCitiesByPostalCodeResponse.cityList.city;
      this.setDictionaryWithSpecificVersion(DictionaryType.CITIES, context, items);
      this.notifyAboutDictionaryUpdate(DictionaryType.CITIES, context);
      if (this.isStreetSelect && this.getSelectedCity(context) && !this.getDictionary(context).dictionariesInitialized) {
        this.requestStreets(context);
      }
    })
  }

  requestStreets(context: AddressDataContext, streetName?: string): void {
    const { postalCode, cityName, cityId } = this.getStreetsRequestParams(context);
    this.insuranceCompanyApiService.findStreets(postalCode, cityName, cityId, streetName).then(resp => {
      const items = resp.findStreetsResponse.streetsList.streetName.map(name => ({streetName: name}));
      this.setDictionaryWithSpecificVersion(DictionaryType.STREETS, context, items);
      this.notifyAboutDictionaryUpdate(DictionaryType.STREETS, context);
      this.getDictionary(context).dictionariesInitialized = true;
    });
  }

  mapAddressData(data: RegistrationData): RegistrationData {
    data.owner.address.city = this.getAddressItemForRegistration(DictionaryType.CITIES, { isCoOwner: false }, <string>data.owner.address.city);
    data.owner.address.street = this.getAddressItemForRegistration(DictionaryType.STREETS, { isCoOwner: false }, <string>data.owner.address.street);

    data.insurer.address.city = this.getAddressItemForRegistration(DictionaryType.CITIES, { isCoOwner: false }, <string>data.insurer.address.city);
    data.insurer.address.street = this.getAddressItemForRegistration(DictionaryType.STREETS, { isCoOwner: false }, <string>data.insurer.address.street);

    if (data.owner.coOwners) {
      data.owner.coOwners.forEach((coOwner, index) => {
        coOwner.address.city = this.getAddressItemForRegistration(DictionaryType.CITIES, { isCoOwner: true, coOwnerIndex: index }, <string>coOwner.address.city);
        coOwner.address.street = this.getAddressItemForRegistration(DictionaryType.STREETS, { isCoOwner: true, coOwnerIndex: index }, <string>coOwner.address.street);
      });
    }

    return data;
  }

  getDictionaryForSpecificVersion(type: DictionaryType, context: AddressDataContext): any[] {
    let { version } = context;
    if (!isNotNullOrUndefined(version)) {
      version = this.getCurrentDictionaryVersion(type, context);
    }
    return this.getDictionary(context)[type][version];
  }

  getCurrentDictionaryVersion(type: DictionaryType, context: AddressDataContext): DictionaryVersion {
    return this.getDictionary(context)[type].currentVersion;
  }

  resetDictionaryToInitial(type: DictionaryType, context: AddressDataContext): void {
    this.notifyAboutDictionaryUpdate(type, {
      ...context,
      version: DictionaryVersion.INITIAL,
      forceSelectClose: true
    });
  }

  getAddressItemConfig(key: InsurerDataConsts): AddressItemConfig {
    switch (key) {
      case InsurerDataConsts.CITY:
        return this.sectionModeConfig.cityConfig;
      case InsurerDataConsts.STREET:
        return this.sectionModeConfig.streetConfig;
    }
  }

  canShowInitialDictionary(fieldKey: string): boolean {
    const addressItemConfig = this.getAddressItemConfig(fieldKey);
    if (isNotNullOrUndefined(addressItemConfig)) {
      return !isNotNullOrUndefined(addressItemConfig.searchTermMinLength) || addressItemConfig.searchTermMinLength === 0;
    }
    return true;
  }

  private clearInsurerData(): void {
    this.registration.registration.insurer = new InsurerRegistrationData();
    this.registrationStorage.updateRegistration();
  }

  private setInsurerDataFromOwner(): void {
    this.registration.registration.insurer = new InsurerRegistrationData();
    this.registration.registration.insurer.address = _.cloneDeep(this.ownerRegistrationData.address);
    this.registrationStorage.updateRegistration();
  }

  private getDictionary(context: AddressDataContext): AddressDataDictionary {
    const { isCoOwner, coOwnerIndex } = context;
    if (isCoOwner) {
      return this.addressDataDictionaries.filter(dict => dict.isCoOwner && dict.coOwnerIndex === coOwnerIndex)[0];
    } else {
      return this.addressDataDictionaries.filter(dict => !dict.isCoOwner)[0];
    }
  }

  private getAddressItemForRegistration(type: DictionaryType, context: AddressDataContext, value: string): AddressItem {
    let item: AddressItem = { name: value };
    let dictionary;
    if (this.isDictionaryMode) {
      dictionary = this.getDictionaryForSpecificVersion(type, context);
    }
    switch (type) {
      case DictionaryType.CITIES:
        let cityId = null;
        if (this.isDictionaryMode) {
          const dictItems = dictionary.filter(city => city.cityName === value);
          if (dictItems.length) {
            cityId = dictItems[0].hasOwnProperty('cityId') ? dictItems[0].cityId : null;
          }
        }
        item.id = cityId;
        return item;
      case DictionaryType.STREETS:
        item.id = null;
        return item;
    }
  }

  private requestCitiesDictionaries(): void {
    this.requestCities({ isCoOwner: false, version: DictionaryVersion.INITIAL });
    this.coownersData.forEach((coOwner, index) => this.requestCities({ isCoOwner: true, coOwnerIndex: index, version: DictionaryVersion.INITIAL }));
  }

  private getStreetsRequestParams(context: AddressDataContext): any {
    let postalCode, cityName, cityId;
    const { isCoOwner, coOwnerIndex } = context;
    if (isCoOwner) {
      postalCode = this.coownersData[coOwnerIndex].address.postalCode;
      cityName = this.coownersRegistrationData[coOwnerIndex].address.city;
    } else {
      postalCode = this.insurerData.address.postalCode;
      cityName = this.insurerRegistrationData!.address['city'];
    }
    const city = this.getDictionaryForSpecificVersion(DictionaryType.CITIES, context).find((cityItem: CityItem) => cityItem.cityName === cityName);
    cityId = city ? city.cityId : null;

    return { postalCode, cityName, cityId };
  }

  private getCitiesRequestParams(context: AddressDataContext): any {
    let postalCode;
    const { isCoOwner, coOwnerIndex } = context;
    if (isCoOwner) {
      postalCode = this.coownersData[coOwnerIndex].address.postalCode;
    } else {
      postalCode = this.insurerData.address.postalCode;
    }

    return { postalCode };
  }

  private resetStreet(context: AddressDataContext): void {
    const { isCoOwner, coOwnerIndex } = context;
    if (!isCoOwner) {
      this.insurerRegistrationData!.address.street = null;
      this.ownerRegistrationData!.address.street = null;
    } else {
      this.coownersRegistrationData[coOwnerIndex]!.address.street = null;
    }
    this.registrationStorage.updateRegistration();
  }

  private notifyAboutDictionaryUpdate(dictionaryType: DictionaryType, context: AddressDataContext): void {
    this.setCurrentDictionaryVersion(dictionaryType, context);
    this.updateDictionaryEvent.emit({
      ...context,
      dictionaryType: dictionaryType
    });
  }

  private getSelectedCity(context: AddressDataContext): any {
    const { isCoOwner, coOwnerIndex } = context;
    if (isCoOwner) {
      return this.coownersRegistrationData[coOwnerIndex]!.address.city;
    } else {
      return this.insurerRegistrationData!.address.city;
    }
  }

  private initAddressDataDictionary(context: AddressDataContext): void {
    this.addressDataDictionaries.push({
      cities: {
        initial: [],
        filtered: [],
        currentVersion: DictionaryVersion.INITIAL
      },
      streets: {
        initial: [],
        filtered: [],
        currentVersion: DictionaryVersion.INITIAL
      },
      dictionariesInitialized: false,
      ...context
    });
    if (this.isDictionaryModeDynamic && this.isStreetSelect && this.getSelectedCity(context) && !this.getDictionary(context).dictionariesInitialized) {
      this.requestStreets(context);
    }
  }

  private setDictionaryWithSpecificVersion(type: DictionaryType, context: AddressDataContext, items: any[]): void {
    let { version } = context;
    if (!isNotNullOrUndefined(version)) {
      version = this.getCurrentDictionaryVersion(type, context);
    }
    this.getDictionary(context)[type][version] = items;
  }

  private setCurrentDictionaryVersion(type: DictionaryType, context: AddressDataContext): void {
    const { version } = context;
    this.getDictionary(context)[type].currentVersion = version;
  }

  private initRequiredStrategy(): void {
    const streetConfig = this.getAddressItemConfig(InsurerDataConsts.STREET);
    if (streetConfig && streetConfig.requiredStrategy) {
      this.streetRequiredStrategy = streetConfig.requiredStrategy;
    } else {
      this.streetRequiredStrategy = RequiredStrategy.ALWAYS_REQUIRED;
    }
  }

}

export interface AddressDataDictionary {
  cities: AddressDataItemDictionary;
  streets: AddressDataItemDictionary;
  isCoOwner: boolean;
  coOwnerIndex?: number;
  dictionariesInitialized: boolean;
}

export interface UpdateDictionaryEvent {
  isCoOwner: boolean;
  coOwnerIndex?: number;
  dictionaryType: DictionaryType;
}

export enum DictionaryType {
  CITIES = 'cities',
  STREETS = 'streets'
}

export enum DictionaryVersion {
  INITIAL = 'initial',
  FILTERED = 'filtered'
}

export interface AddressDataContext {
  isCoOwner: boolean;
  coOwnerIndex?: number;
  forceSelectClose?: boolean;
  version?: DictionaryVersion;
}

export interface AddressDataItemDictionary {
  initial: any[];
  filtered: any[];
  currentVersion: DictionaryVersion;
}
